import React, { useState, useEffect } from "react";
import { Card, CardBody, CardHeader, Col, Row, Label } from "reactstrap";
import { AttendanceCharts } from "./DashboardEcommerceCharts";
import CountUp from "react-countup";
import moment from "moment";
import Flatpickr from "react-flatpickr";
import {
  adminGetDashboardAttendance,
  adminGetDashboardAttendanceByPhoto,
} from "helpers/backend_helpers";

type AttendanceType = "QR" | "SELFIE";

interface AttendanceData {
  date: string;
  late: { count: number; members: string[] };
  on_time: { count: number; members: string[] };
  tolerance: { count: number; members: string[] };
}

interface Summary {
  total_late: number;
  total_on_time: number;
  total_tolerance: number;
}

const Revenue = () => {
  const [filterDate, setFilterDate] = useState<Date[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [summary, setSummary] = useState<Summary>({
    total_late: 0,
    total_on_time: 0,
    total_tolerance: 0,
  });
  const [loading, setLoading] = useState(false);

  // Get attendance type from sessionStorage
  const user: any = JSON.parse(sessionStorage.getItem("user") || "null");
  const role: any = sessionStorage.getItem("userRole") || "";
  const attendanceType =
    role === "superadmin" ? user?.attendanceType : user?.companyAttendanceType;

  // Validate date range (max 30 days)
  const validateDateRange = (dates: Date[]): boolean => {
    if (dates.length !== 2) return true;

    const startDate = moment(dates[0]);
    const endDate = moment(dates[1]);
    const daysDiff = endDate.diff(startDate, "days");

    return daysDiff <= 30;
  };

  // Transform API data to chart format - ALL BAR CHARTS
  const transformDataToChart = (data: AttendanceData[]) => {
    // Sort data by date
    const sortedData = [...data].sort(
      (a, b) => moment(a.date).valueOf() - moment(b.date).valueOf(),
    );

    const categories = sortedData.map((item) =>
      moment(item.date).format("DD MMM YYYY"),
    );

    const onTimeData = sortedData.map((item) => item.on_time.count);
    const onTimeMembers = sortedData.map((item) => item.on_time.members);

    const toleranceData = sortedData.map((item) => item.tolerance.count);
    const toleranceMembers = sortedData.map((item) => item.tolerance.members);

    const lateData = sortedData.map((item) => item.late.count);
    const lateMembers = sortedData.map((item) => item.late.members);

    return {
      categories,
      series: [
        {
          name: "Tepat Waktu",
          type: "column",
          data: onTimeData,
          members: onTimeMembers,
        },
        {
          name: "Toleransi",
          type: "column",
          data: toleranceData,
          members: toleranceMembers,
        },
        {
          name: "Terlambat",
          type: "column",
          data: lateData,
          members: lateMembers,
        },
      ],
    };
  };

  // Fetch attendance data
  const fetchAttendanceData = async () => {
    if (filterDate.length !== 2) return;

    if (!validateDateRange(filterDate)) {
      alert("Date range cannot exceed 30 days!");
      return;
    }

    setLoading(true);

    try {
      const query = {
        startDate: moment(filterDate[0]).format("YYYY-MM-DD"),
        endDate: moment(filterDate[1]).format("YYYY-MM-DD"),
      };

      const endpoint =
        attendanceType === "SELFIE"
          ? adminGetDashboardAttendanceByPhoto
          : adminGetDashboardAttendance;

      const response: any = await endpoint(query);

      if (response?.data?.data) {
        const transformedData = transformDataToChart(response.data.data);
        setChartData(transformedData.series);
        setCategories(transformedData.categories);
        setSummary(
          response.data?.summary || {
            total_late: 0,
            total_on_time: 0,
            total_tolerance: 0,
          },
        );
      }
    } catch (error) {
      console.error("Error fetching attendance data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on date change
  useEffect(() => {
    if (filterDate.length === 2 && attendanceType) {
      fetchAttendanceData();
    }
  }, [filterDate]);

  // Set default date range (last 7 days)
  useEffect(() => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    setFilterDate([startDate, endDate]);
  }, []);

  const handleDateChange = (dates: Date[]) => {
    if (dates.length === 2 && !validateDateRange(dates)) {
      alert("Date range cannot exceed 30 days!");
      return;
    }
    setFilterDate(dates);
  };

  return (
    <React.Fragment>
      <Card>
        <CardHeader className="border-0 align-items-center d-flex">
          <h4 className="card-title mb-0 flex-grow-1">
            Riwayat Kehadiran
            {/* {attendanceType && `(${attendanceType})`} */}
          </h4>
          <div className="d-flex gap-2 align-items-center">
            <div style={{ minWidth: "250px" }}>
              <Flatpickr
                className="form-control"
                options={{
                  mode: "range",
                  dateFormat: "Y-m-d",
                  maxDate: new Date(),
                }}
                value={filterDate}
                onChange={handleDateChange}
                placeholder="Select date range"
              />
            </div>
          </div>
        </CardHeader>

        <CardHeader className="p-0 border-0 bg-light-subtle">
          <Row className="g-0 text-center">
            <Col xs={6} sm={4}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1 text-success">
                  <CountUp start={0} end={summary.total_on_time} duration={1} />
                </h5>
                <p className="text-muted mb-0">Tepat Waktu</p>
              </div>
            </Col>
            <Col xs={6} sm={4}>
              <div className="p-3 border border-dashed border-start-0">
                <h5 className="mb-1 text-warning">
                  <CountUp
                    start={0}
                    end={summary.total_tolerance}
                    duration={1}
                  />
                </h5>
                <p className="text-muted mb-0">Toleransi</p>
              </div>
            </Col>
            <Col xs={6} sm={4}>
              <div className="p-3 border border-dashed border-start-0 border-end-0">
                <h5 className="mb-1 text-danger">
                  <CountUp start={0} end={summary.total_late} duration={1} />
                </h5>
                <p className="text-muted mb-0">Terlambat</p>
              </div>
            </Col>
          </Row>
        </CardHeader>

        <CardBody className="p-0 pb-2">
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : chartData.length > 0 ? (
            <div className="w-100">
              <div dir="ltr">
                <AttendanceCharts
                  series={chartData}
                  categories={categories}
                  dataColors='["--vz-success", "--vz-warning", "--vz-danger"]'
                />
              </div>
            </div>
          ) : (
            <div className="text-center p-5">
              <p className="text-muted">
                Silahkan pilih rentang tanggal untuk melihat data kehadiran
              </p>
            </div>
          )}
        </CardBody>
      </Card>
    </React.Fragment>
  );
};

export default Revenue;
